import { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { LoadingWrapperContext } from 'src/modules/core/context'
import { SyncOfflineChangesModal } from 'src/modules/user/components/send-test-or-finish/sync-offline-changes-modal'
import { useProfile } from 'src/modules/user/hooks/use-profile'

/**
 * A direct destination for the offline-grant ask, for callers that already know
 * the grant is the next step.
 *
 * The add-on's consent card is the caller this exists for. Deep-linking it to
 * `/dashboard/home` did not work: `resolveRequirements` evaluates the
 * regular-onboarding redirect before the offline-sync render, so an account that
 * has not completed the web onboarding was bounced to
 * `/onboarding/configuration` and asked to onboard a second time, which is the
 * exact momentum loss the add-on onboarding exists to avoid.
 *
 * This route sits outside `isRouteWithCompletedOnboarding` and is excluded from
 * `isOnboardingRoute`, so neither `evaluateRouteAccess` nor
 * `resolveRequirements` diverts it in either direction: the half-onboarded
 * account is not sent back to configuration, and the fully onboarded one is not
 * bounced to the dashboard to be shown the same modal there. The grant is asked
 * for straight away, whichever the account is. The modal itself is untouched: it
 * is still the same component the dashboard gate renders, so there is one
 * implementation of the logout, Google redirect and token-exchange sequence.
 *
 * An account that already holds a refresh token has nothing to do here, so it is
 * sent to the dashboard rather than shown an ask it has already answered.
 */
export function SyncCalendar() {
  const { disableLoading } = useContext(LoadingWrapperContext)
  const navigate = useNavigate()
  const profile = useProfile()
  const hasOfflineGrant = Boolean(profile.data?.refresh_token)

  useEffect(() => {
    disableLoading()
  }, [disableLoading])

  useEffect(() => {
    if (hasOfflineGrant) {
      navigate(routeNames.dashboard.path, { replace: true })
    }
  }, [hasOfflineGrant, navigate])

  if (profile.isLoading || hasOfflineGrant) {
    return <Loader isLoading full />
  }

  return <SyncOfflineChangesModal />
}
